import { Chart } from 'chart.js/auto';
import React, { useRef } from 'react';

import P from '@hero/ui-kit/typography/P';
import Layout from '../../components/Layout';
import LocationFilter from '../../components/LocationFilter';
import NpsRating from '../../components/NpsRating';
import PageNotFound from '../../components/PageNotFound';
import SectionNav, { SectionNavItem } from '../../components/SectionNav';
import useGetProviderDetails from '../../api/useGetProviderDetails';
import useScrollSpy from '../../hooks/useScrollSpy';
import { useUserStatusContext } from '../../context/UserStatus';
import * as Style from './style.module.scss';
import useGetFormFillBacklogAgeing from './api/useGetFormFillBacklogAgeing';
import useGetFormFillCloseRate from './api/useGetFormFillCloseRate';
import useGetFormFillTAT from './api/useGetFormFillTAT';
import useGetNetDischarge from './api/useGetNetDischarge';
import useGetProviderScoreWeeks from './api/useGetProviderScoreWeeks';
import Tooltip from '@hero/ui-kit/components/Tooltip';
import InfoIcon from '@hero/ui-kit/icons/utility/InfoIcon';

// the score API has no state dimension, so LocationFilter cannot narrow those five series
const ORG_WIDE = 'Organization-wide - not affected by the location filter';

// one entry per card, in render order; the label doubles as the card heading so the nav and the
// heading cannot drift apart
const SECTIONS: (SectionNavItem & { note?: string; tooltip: React.ReactNode })[] = [
    {
        id: 'provider-performance-score',
        label: 'Provider Performance Score',
        note: ORG_WIDE,
        tooltip:
            'Overall performance score, on a 0-5 scale, blending NPS, Time to Enrol, Activation Rate, and Churn Rate into one composite trend line.'
    },
    {
        id: 'nps-score',
        label: 'NPS Score',
        note: ORG_WIDE,
        tooltip: (
            <>
                The Net Promoter Score component, derived from the raw NPS value for the period.
                <br /> Mapping: NPS ≥ 85 maps to 5. From 85 to 0, the score scales linearly down to
                0.
            </>
        )
    },
    {
        id: 'time-to-enrol-score',
        label: 'Time to Enrol Score',
        note: ORG_WIDE,
        tooltip: (
            <>
                Tracks how fast enrollments are completed - average days from form fill to
                enrollment.
                <br /> Mapping: Average TTE ≤ 10 days maps to 5. Average TTE ≥ 20 days maps to 1.
            </>
        )
    },
    {
        id: 'activation-rate',
        label: 'Activation Rate',
        note: ORG_WIDE,
        tooltip: (
            <>
                The share of enrolled members who go on to actually start using Hero.
                <br /> Mapping: Activation Rate ≥ 90% maps to 5. ≤ 75% maps to 1. Linear in between
                (15-point span across 4 points → 4 / 0.15).
            </>
        )
    },
    {
        id: 'churn-rate',
        label: 'Churn Rate',
        note: ORG_WIDE,
        tooltip: (
            <>
                The share of your active base that was discharged in the period - higher scores
                indicate lower (better) churn relative to target.
                <br /> Mapping: Churn Rate ≤ 1.5% maps to 5. ≥ 3.5% maps to 1. Linear in between
                (2-point span across 4 points → 4/0.02.
            </>
        )
    },
    {
        id: 'close-rate',
        label: 'Form fills - Close rate',
        tooltip:
            'Shows how quickly and successfully submitted forms get resolved (either Accepted or Rejected) - split into Accepted, Rejected, Expired, and still-Open.'
    },
    {
        id: 'tat-to-enroll',
        label: 'Form fills - TAT to Enroll',
        tooltip:
            'Measures turnaround time in days from when a form is submitted to when enrollment is completed.'
    },
    {
        id: 'backlog-ageing',
        label: 'Form fills - Backlog Ageing',
        tooltip:
            'Measures how long open/unresolved form fills have been sitting in the backlog, bucketed by age (e.g., 0-7, 7-14, 14+ days) to flag aging or stalled cases.'
    },
    {
        id: 'net-discharge',
        label: 'Net Discharge',
        tooltip:
            'Net change in discharged members for the period - total discharges minus any re-enrollments or channel change.'
    }
];

const SECTION_IDS = SECTIONS.map((section) => section.id);

const NPS_TARGET = 85; //  the raw NPS that scores a full 5.00
const TTE_TARGET_DAYS = 10; //  the avg TTE at or below which the score saturates at 5.00

const CHART_COLORS = {
    red: 'rgb(255, 99, 132)',
    orange: 'rgb(255, 159, 64)',
    yellow: 'rgb(255, 205, 86)',
    green: 'rgba(81, 192, 75, 1)',
    turquoise: 'rgb(75, 192, 192)',
    blue: 'rgb(54, 162, 235)',
    purple: 'rgb(153, 102, 255)',
    grey: 'rgb(201, 203, 207)',
    black: 'rgba(67, 67, 67, 1)'
};

const transparentize = (color: string, opacity: number) => {
    const alpha = 1 - opacity;
    return color.replace('rgb', 'rgba').replace(')', `, ${alpha})`);
};

const OurPerformanceContent: React.FC = () => {
    const { userStatus } = useUserStatusContext();
    const { data: providerDetails } = useGetProviderDetails(
        userStatus?.hero_organization_id
            ? { hero_organization_id: userStatus.hero_organization_id }
            : undefined
    );

    const chart1CanvasRef = useRef<HTMLCanvasElement>(null);
    const chart2CanvasRef = useRef<HTMLCanvasElement>(null);
    const chart3CanvasRef = useRef<HTMLCanvasElement>(null);
    const chart4CanvasRef = useRef<HTMLCanvasElement>(null);
    const chart5CanvasRef = useRef<HTMLCanvasElement>(null);
    const chart6CanvasRef = useRef<HTMLCanvasElement>(null);
    const chart7CanvasRef = useRef<HTMLCanvasElement>(null);
    const chart8CanvasRef = useRef<HTMLCanvasElement>(null);
    const chart9CanvasRef = useRef<HTMLCanvasElement>(null);

    const chart1Ref = useRef<Chart | undefined>();
    const chart2Ref = useRef<Chart | undefined>();
    const chart3Ref = useRef<Chart | undefined>();
    const chart4Ref = useRef<Chart | undefined>();
    const chart5Ref = useRef<Chart | undefined>();
    const chart6Ref = useRef<Chart | undefined>();
    const chart7Ref = useRef<Chart | undefined>();
    const chart8Ref = useRef<Chart | undefined>();
    const chart9Ref = useRef<Chart | undefined>();

    // keyed by section id rather than position so SECTIONS can be reordered freely
    const sectionCanvasRefs: Record<string, React.RefObject<HTMLCanvasElement>> = {
        'close-rate': chart1CanvasRef,
        'tat-to-enroll': chart2CanvasRef,
        'backlog-ageing': chart3CanvasRef,
        'net-discharge': chart4CanvasRef,
        'nps-score': chart5CanvasRef,
        'time-to-enrol-score': chart6CanvasRef,
        'activation-rate': chart7CanvasRef,
        'churn-rate': chart8CanvasRef,
        'provider-performance-score': chart9CanvasRef
    };

    const activeSection = useScrollSpy(SECTION_IDS);

    const formFillCloseRate = useGetFormFillCloseRate();
    const formFillTAT = useGetFormFillTAT();
    const formFillBacklogAgeing = useGetFormFillBacklogAgeing();
    const netDischarge = useGetNetDischarge();
    const providerScoreWeeks = useGetProviderScoreWeeks();

    // Chart 1: Form fills - Close rate
    React.useEffect(() => {
        if (!chart1CanvasRef.current) {
            return;
        }

        const data = formFillCloseRate
            ? {
                  labels: formFillCloseRate.map((row) => row.week_start),
                  datasets: [
                      {
                          type: 'line' as const,
                          label: '% Closed Target',
                          data: formFillCloseRate.map((_) => 95),
                          borderColor: CHART_COLORS.black,
                          borderDash: [8, 4],
                          yAxisID: 'y1'
                      },
                      {
                          type: 'line' as const,
                          label: '% Closed',
                          data: formFillCloseRate.map((row) => row.expiration_rate),
                          borderColor: CHART_COLORS.turquoise,
                          yAxisID: 'y1'
                      },
                      {
                          type: 'line' as const,
                          label: '% Accepted',
                          data: formFillCloseRate.map((row) => row.conversion_rate),
                          borderColor: CHART_COLORS.blue,
                          yAxisID: 'y1'
                      },
                      {
                          type: 'bar' as const,
                          label: 'Accepted',
                          data: formFillCloseRate.map((row) => row.enrolled_form_fills),
                          backgroundColor: CHART_COLORS.green,
                          yAxisID: 'y'
                      },
                      {
                          type: 'bar' as const,
                          label: 'Rejected',
                          data: formFillCloseRate.map((row) => row.rejected_form_fills),
                          backgroundColor: CHART_COLORS.yellow,
                          yAxisID: 'y'
                      },
                      {
                          type: 'bar' as const,
                          label: 'Expired',
                          data: formFillCloseRate.map((row) => row.expired_form_fills),
                          backgroundColor: CHART_COLORS.orange,
                          yAxisID: 'y'
                      },
                      {
                          type: 'bar' as const,
                          label: 'Open',
                          data: formFillCloseRate.map((row) => row.open_form_fills),
                          backgroundColor: CHART_COLORS.grey,
                          yAxisID: 'y'
                      }
                  ]
              }
            : { datasets: [] };

        // Update chart data if chart exists and data is available
        if (chart1Ref.current && formFillCloseRate) {
            chart1Ref.current.data = data;
            chart1Ref.current.update('active');

            return;
        }

        if (!chart1Ref.current) {
            chart1Ref.current = new Chart(chart1CanvasRef.current, {
                data,
                options: {
                    scales: {
                        x: {
                            stacked: true,
                            offset: true,
                            ticks: {
                                callback: function (value) {
                                    return this.getLabelForValue(value as number);
                                }
                            }
                        },
                        y: {
                            beginAtZero: true,
                            position: 'left',
                            stacked: true,
                            grid: {
                                display: false
                            }
                        },
                        y1: {
                            beginAtZero: true,
                            position: 'right',
                            ticks: {
                                callback: function (value) {
                                    return value + '%';
                                }
                            }
                        }
                    }
                }
            });
        }
    }, [formFillCloseRate]);

    // Chart 2: Form fills - TAT Enrolled
    React.useEffect(() => {
        if (!chart2CanvasRef.current) {
            return;
        }

        const data = formFillTAT
            ? {
                  labels: formFillTAT.map((row) => row.week_start),
                  datasets: [
                      {
                          type: 'line' as const,
                          label: 'Average TAT to Enroll',
                          data: formFillTAT.map((row) => row.average_tat_to_enroll_days),
                          borderColor: CHART_COLORS.yellow,
                          yAxisID: 'y1'
                      },
                      {
                          type: 'bar' as const,
                          label: 'Enrolled Form Fills',
                          data: formFillTAT.map((row) => row.enrolled_form_fills),
                          backgroundColor: CHART_COLORS.green,
                          yAxisID: 'y'
                      }
                  ]
              }
            : { datasets: [] };

        // Update chart data if chart exists and data is available
        if (chart2Ref.current && formFillTAT) {
            chart2Ref.current.data = data;
            chart2Ref.current.update('active');

            return;
        }

        if (!chart2Ref.current) {
            chart2Ref.current = new Chart(chart2CanvasRef.current, {
                data,
                options: {
                    scales: {
                        y: {
                            beginAtZero: true,
                            position: 'left',
                            grid: {
                                display: false
                            }
                        },
                        y1: {
                            beginAtZero: true,
                            position: 'right'
                        }
                    }
                }
            });
        }
    }, [formFillTAT]);

    // Chart 3: Form fills - Backlog Ageing
    React.useEffect(() => {
        if (!chart3CanvasRef.current) {
            return;
        }

        const data = formFillBacklogAgeing
            ? {
                  labels: formFillBacklogAgeing.map((row) => row.week_start),
                  datasets: [
                      {
                          type: 'line' as const,
                          label: 'Average Age',
                          data: formFillBacklogAgeing.map((row) => row.average_age_days),
                          borderColor: CHART_COLORS.blue,
                          yAxisID: 'y1'
                      },
                      {
                          type: 'bar' as const,
                          label: '0-7 Days',
                          data: formFillBacklogAgeing.map((row) => row.open_form_fills_0_to_7_days),
                          backgroundColor: CHART_COLORS.green,
                          yAxisID: 'y',
                          stack: 'backlog'
                      },
                      {
                          type: 'bar' as const,
                          label: '7-14 Days',
                          data: formFillBacklogAgeing.map(
                              (row) => row.open_form_fills_7_to_14_days
                          ),
                          backgroundColor: CHART_COLORS.yellow,
                          yAxisID: 'y',
                          stack: 'backlog'
                      },
                      {
                          type: 'bar' as const,
                          label: '14+ Days',
                          data: formFillBacklogAgeing.map(
                              (row) => row.open_form_fills_14_plus_days
                          ),
                          backgroundColor: CHART_COLORS.orange,
                          yAxisID: 'y',
                          stack: 'backlog'
                      }
                  ]
              }
            : { datasets: [] };

        // Update chart data if chart exists and data is available
        if (chart3Ref.current && formFillBacklogAgeing) {
            chart3Ref.current.data = data;
            chart3Ref.current.update('active');

            return;
        }

        if (!chart3Ref.current) {
            chart3Ref.current = new Chart(chart3CanvasRef.current, {
                data,
                options: {
                    scales: {
                        y: {
                            beginAtZero: true,
                            position: 'left',
                            stacked: true,
                            grid: {
                                display: false
                            }
                        },
                        y1: {
                            beginAtZero: true,
                            position: 'right'
                        }
                    }
                }
            });
        }
    }, [formFillBacklogAgeing]);

    // Chart 4: Members - Net Discharge Rate
    React.useEffect(() => {
        if (!chart4CanvasRef.current) {
            return;
        }

        const data = netDischarge
            ? {
                  labels: netDischarge.map((row) => row.week_start),
                  datasets: [
                      {
                          type: 'bar' as const,
                          label: 'Net Discharged',
                          data: netDischarge.map((row) => row.discharged_members),
                          backgroundColor: transparentize(CHART_COLORS.red, 0.5),
                          yAxisID: 'y'
                      },
                      {
                          type: 'line' as const,
                          label: 'Net Discharged Rate',
                          data: netDischarge.map((row) => row.net_discharge_rate),
                          borderColor: CHART_COLORS.red,
                          yAxisID: 'y1'
                      }
                  ]
              }
            : { datasets: [] };

        // Update chart data if chart exists and data is available
        if (chart4Ref.current && netDischarge) {
            chart4Ref.current.data = data;
            chart4Ref.current.update('active');

            return;
        }

        if (!chart4Ref.current) {
            chart4Ref.current = new Chart(chart4CanvasRef.current, {
                data,
                options: {
                    scales: {
                        y: {
                            beginAtZero: true,
                            position: 'left',
                            grid: {
                                display: false
                            }
                        },
                        y1: {
                            beginAtZero: true,
                            position: 'right',
                            ticks: {
                                callback: function (value) {
                                    return value + '%';
                                }
                            }
                        }
                    }
                }
            });
        }
    }, [netDischarge]);

    // Chart 5: NPS Score
    React.useEffect(() => {
        if (!chart5CanvasRef.current) {
            return;
        }

        const data = providerScoreWeeks
            ? {
                  labels: providerScoreWeeks.map((row) => row.week_start),
                  datasets: [
                      {
                          type: 'line' as const,
                          label: 'NPS Score (0-5)',
                          data: providerScoreWeeks.map((row) => row.nps_kpi_score),
                          borderColor: CHART_COLORS.green,
                          yAxisID: 'y1'
                      },
                      {
                          type: 'line' as const,
                          label: 'NPS Target',
                          data: providerScoreWeeks.map((_) => NPS_TARGET),
                          borderColor: CHART_COLORS.orange,
                          pointRadius: 0,
                          yAxisID: 'y'
                      },
                      {
                          type: 'bar' as const,
                          label: 'NPS',
                          data: providerScoreWeeks.map((row) => row.nps_score),
                          backgroundColor: transparentize(CHART_COLORS.blue, 0.3),
                          yAxisID: 'y'
                      }
                  ]
              }
            : { datasets: [] };

        // Update chart data if chart exists and data is available
        if (chart5Ref.current && providerScoreWeeks) {
            chart5Ref.current.data = data;
            chart5Ref.current.update('active');

            return;
        }

        if (!chart5Ref.current) {
            chart5Ref.current = new Chart(chart5CanvasRef.current, {
                data,
                options: {
                    scales: {
                        y: {
                            beginAtZero: true,
                            position: 'left',
                            max: 100,
                            title: {
                                display: true,
                                text: 'NPS'
                            },
                            grid: {
                                display: false
                            }
                        },
                        y1: {
                            beginAtZero: true,
                            position: 'right',
                            max: 5,
                            title: {
                                display: true,
                                text: 'NPS Score'
                            },
                            ticks: {
                                stepSize: 1,
                                callback: function (value) {
                                    return Number(value).toFixed(2);
                                }
                            }
                        }
                    }
                }
            });
        }
    }, [providerScoreWeeks]);

    // Chart 6: Time to Enrol Score
    React.useEffect(() => {
        if (!chart6CanvasRef.current) {
            return;
        }

        const data = providerScoreWeeks
            ? {
                  labels: providerScoreWeeks.map((row) => row.week_start),
                  datasets: [
                      {
                          type: 'line' as const,
                          label: 'Avg. Time to Enroll',
                          data: providerScoreWeeks.map((row) => row.avg_tte_days),
                          borderColor: CHART_COLORS.yellow,
                          backgroundColor: transparentize(CHART_COLORS.yellow, 0.8),
                          fill: 'origin' as const,
                          // one plateau per week, changing on the category boundary
                          stepped: 'middle' as const,
                          pointRadius: 0,
                          order: 0,
                          yAxisID: 'y'
                      },
                      {
                          type: 'bar' as const,
                          label: 'Enrolled F-F',
                          data: providerScoreWeeks.map((row) => row.enrolled_form_fills),
                          backgroundColor: CHART_COLORS.green,
                          order: 1,
                          yAxisID: 'y'
                      },
                      {
                          type: 'line' as const,
                          label: 'Target Time',
                          data: providerScoreWeeks.map((_) => TTE_TARGET_DAYS),
                          borderColor: CHART_COLORS.orange,
                          pointRadius: 0,
                          order: 2,
                          yAxisID: 'y'
                      },
                      {
                          type: 'line' as const,
                          label: 'TTE Score',
                          data: providerScoreWeeks.map((row) => row.tte_kpi_score),
                          borderColor: CHART_COLORS.blue,
                          pointRadius: 0,
                          order: 3,
                          yAxisID: 'y1'
                      }
                  ]
              }
            : { datasets: [] };

        // Update chart data if chart exists and data is available
        if (chart6Ref.current && providerScoreWeeks) {
            chart6Ref.current.data = data;
            chart6Ref.current.update('active');

            return;
        }

        if (!chart6Ref.current) {
            chart6Ref.current = new Chart(chart6CanvasRef.current, {
                data,
                options: {
                    scales: {
                        y: {
                            beginAtZero: true,
                            position: 'left',
                            suggestedMax: 60,
                            ticks: {
                                stepSize: 20
                            },
                            title: {
                                display: true,
                                text: 'Enrolled F-F/Avg. TTE'
                            },
                            grid: {
                                display: false
                            }
                        },
                        y1: {
                            beginAtZero: true,
                            position: 'right',
                            max: 5,
                            ticks: {
                                stepSize: 1
                            },
                            title: {
                                display: true,
                                text: 'TTE Score'
                            }
                        }
                    }
                }
            });
        }
    }, [providerScoreWeeks]);

    // Chart 7: Activation Rate
    React.useEffect(() => {
        if (!chart7CanvasRef.current) {
            return;
        }

        const data = providerScoreWeeks
            ? {
                  labels: providerScoreWeeks.map((row) => row.week_start),
                  datasets: [
                      {
                          type: 'line' as const,
                          label: 'AR Score (0-5)',
                          data: providerScoreWeeks.map((row) => row.activation_kpi_score),
                          borderColor: CHART_COLORS.orange,
                          pointRadius: 0,
                          yAxisID: 'y1'
                      },
                      {
                          type: 'bar' as const,
                          label: 'Activation Rate',
                          data: providerScoreWeeks.map((row) => row.activation_rate),
                          backgroundColor: transparentize(CHART_COLORS.blue, 0.3),
                          yAxisID: 'y'
                      }
                  ]
              }
            : { datasets: [] };

        // Update chart data if chart exists and data is available
        if (chart7Ref.current && providerScoreWeeks) {
            chart7Ref.current.data = data;
            chart7Ref.current.update('active');

            return;
        }

        if (!chart7Ref.current) {
            chart7Ref.current = new Chart(chart7CanvasRef.current, {
                data,
                options: {
                    scales: {
                        y: {
                            beginAtZero: true,
                            position: 'left',
                            max: 1,
                            ticks: {
                                stepSize: 0.25,
                                callback: function (value) {
                                    return Number(value).toFixed(2);
                                }
                            },
                            title: {
                                display: true,
                                text: 'Activation Rate'
                            },
                            grid: {
                                display: false
                            }
                        },
                        y1: {
                            beginAtZero: true,
                            position: 'right',
                            max: 5,
                            ticks: {
                                stepSize: 1,
                                callback: function (value) {
                                    return Number(value).toFixed(2);
                                }
                            },
                            title: {
                                display: true,
                                text: 'Activation Rate Score'
                            }
                        }
                    }
                }
            });
        }
    }, [providerScoreWeeks]);

    // Chart 8: Churn Rate
    React.useEffect(() => {
        if (!chart8CanvasRef.current) {
            return;
        }

        const data = providerScoreWeeks
            ? {
                  labels: providerScoreWeeks.map((row) => row.week_start),
                  datasets: [
                      {
                          type: 'line' as const,
                          label: 'Churn Rate Score (0-5)',
                          data: providerScoreWeeks.map((row) => row.churn_kpi_score),
                          borderColor: CHART_COLORS.blue,
                          pointRadius: 0,
                          yAxisID: 'y1'
                      },
                      {
                          type: 'bar' as const,
                          label: 'Churn Rate',
                          data: providerScoreWeeks.map((row) => row.churn_rate),
                          backgroundColor: transparentize(CHART_COLORS.blue, 0.3),
                          yAxisID: 'y'
                      }
                  ]
              }
            : { datasets: [] };

        // Update chart data if chart exists and data is available
        if (chart8Ref.current && providerScoreWeeks) {
            chart8Ref.current.data = data;
            chart8Ref.current.update('active');

            return;
        }

        if (!chart8Ref.current) {
            chart8Ref.current = new Chart(chart8CanvasRef.current, {
                data,
                options: {
                    scales: {
                        y: {
                            beginAtZero: true,
                            position: 'left',
                            // churn floors the score at 0.035, so a 0-1 axis would flatten the series
                            // into an invisible sliver; 0.05 covers the whole scored band with headroom
                            suggestedMax: 0.05,
                            title: {
                                display: true,
                                text: 'Churn Rate'
                            },
                            grid: {
                                display: false
                            }
                        },
                        y1: {
                            beginAtZero: true,
                            position: 'right',
                            max: 5,
                            ticks: {
                                stepSize: 1,
                                callback: function (value) {
                                    return Number(value).toFixed(2);
                                }
                            },
                            title: {
                                display: true,
                                text: 'Churn Rate Score'
                            }
                        }
                    }
                }
            });
        }
    }, [providerScoreWeeks]);

    // Chart 9: Provider Performance Score
    React.useEffect(() => {
        if (!chart9CanvasRef.current) {
            return;
        }

        const data = providerScoreWeeks
            ? {
                  labels: providerScoreWeeks.map((row) => row.week_start),
                  datasets: [
                      {
                          type: 'line' as const,
                          label: 'NPS Score',
                          data: providerScoreWeeks.map((row) => row.nps_kpi_score),
                          borderColor: CHART_COLORS.green,
                          pointRadius: 0
                      },
                      {
                          type: 'line' as const,
                          label: 'TTE Score',
                          data: providerScoreWeeks.map((row) => row.tte_kpi_score),
                          borderColor: transparentize(CHART_COLORS.red, 0.5),
                          pointRadius: 0
                      },
                      {
                          type: 'line' as const,
                          label: 'AR Score',
                          data: providerScoreWeeks.map((row) => row.activation_kpi_score),
                          borderColor: CHART_COLORS.orange,
                          pointRadius: 0
                      },
                      {
                          type: 'line' as const,
                          label: 'Churn Rate Score',
                          data: providerScoreWeeks.map((row) => row.churn_kpi_score),
                          borderColor: CHART_COLORS.blue,
                          pointRadius: 0
                      },
                      {
                          type: 'line' as const,
                          label: 'Combined Score',
                          data: providerScoreWeeks.map((row) => row.composite_score),
                          borderColor: CHART_COLORS.red,
                          borderWidth: 5,
                          pointRadius: 0
                      }
                  ]
              }
            : { datasets: [] };

        // Update chart data if chart exists and data is available
        if (chart9Ref.current && providerScoreWeeks) {
            chart9Ref.current.data = data;
            chart9Ref.current.update('active');

            return;
        }

        if (!chart9Ref.current) {
            chart9Ref.current = new Chart(chart9CanvasRef.current, {
                data,
                options: {
                    scales: {
                        y: {
                            beginAtZero: true,
                            max: 6,
                            ticks: {
                                stepSize: 2
                            },
                            title: {
                                display: true,
                                text: 'Score'
                            }
                        }
                    }
                }
            });
        }
    }, [providerScoreWeeks]);

    // Chart.js keeps listeners on a canvas React has already removed; the refs are cleared too so a
    // remount rebuilds instead of updating a destroyed instance.
    React.useEffect(
        () => () => {
            const charts = [
                chart1Ref,
                chart2Ref,
                chart3Ref,
                chart4Ref,
                chart5Ref,
                chart6Ref,
                chart7Ref,
                chart8Ref,
                chart9Ref
            ];

            for (const chart of charts) {
                chart.current?.destroy();
                chart.current = undefined;
            }
        },
        []
    );

    return (
        <Layout
            title="Our Performance"
            additionalElement={<LocationFilter />}
            titleAddon={<NpsRating npsRate={providerDetails?.nps_rate ?? null} />}
        >
            <div className={Style.layout}>
                <div className={Style.nav}>
                    <SectionNav items={SECTIONS} activeId={activeSection} />
                </div>

                <div className={Style.sections}>
                    {SECTIONS.map((section) => (
                        <div key={section.id} id={section.id} className={Style.card}>
                            <h5>{section.label}</h5>
                            {section.note ? (
                                <P size="small" noDefaultMargin>
                                    {section.note}
                                </P>
                            ) : null}
                            <Tooltip
                                content={section.tooltip}
                                width={380}
                                placement="bottom-right"
                                className={Style.tooltip}
                            >
                                <button type="button" aria-label={'label'}>
                                    <InfoIcon size="small" />
                                </button>
                            </Tooltip>
                            <hr />
                            <canvas ref={sectionCanvasRefs[section.id]}></canvas>
                        </div>
                    ))}
                </div>
            </div>
        </Layout>
    );
};

// guard lives in a wrapper so the KPI queries below never fire without permission
const OurPerformance: React.FC = () => {
    const { permissions } = useUserStatusContext();

    return permissions.kpiReports.kpiReportsView ? <OurPerformanceContent /> : <PageNotFound />;
};

export default OurPerformance;
